import type { MoveRef, RecruitOutcome } from '@/schemas/character';

/**
 * **FI-35 (D-FI35)** — Helper SSOT per la proiezione `MoveRef ↔
 * choiceGroupsByMove` e le mutazioni del setup di una mossa.
 *
 * **Why**: prima del refactor questi 4 helper erano duplicati in 3 punti:
 *  - `SectionStartingMoves.buildMoveRef` (builder L1)
 *  - `LevelUpStepMove` helpers (`updateMoveChoiceGroup` / `updateMoveNarrativeField`)
 *  - `LevelUpStepGranted` helpers (`updateSetupChoiceGroup` / `updateSetupNarrativeField`)
 *
 * Estratti qui per centralizzare l'unica forma corretta della proiezione
 * (`'main'` virtuale ↔ `selectedChoices` shorthand) e ridurre il rischio
 * di disallineamento.
 */

/**
 * Proietta un `MoveRef` nella shape che `MoveSetupEditor` consuma:
 * `Record<moveId, Record<groupId, selectedIds>>`.
 *
 * - Le `selectedChoices` shorthand vengono esposte come gruppo virtuale
 *   `'main'` se non già presente in `selectedChoiceGroups`.
 * - Il `MoveRef` di input può essere `undefined` (nessuna mossa selezionata):
 *   ritorna mappa vuota.
 */
export function buildChoiceGroupsByMove(
  moveRef: MoveRef | undefined,
): Record<string, Record<string, string[]>> {
  if (!moveRef) return {};
  const groups: Record<string, string[]> = { ...(moveRef.selectedChoiceGroups ?? {}) };
  if (moveRef.selectedChoices.length > 0 && groups.main === undefined) {
    groups.main = [...moveRef.selectedChoices];
  }
  return { [moveRef.id]: groups };
}

/**
 * Proietta un `MoveRef` nella shape di `narrativeFieldsByMove` che
 * `MoveSetupEditor` consuma.
 */
export function buildNarrativeFieldsByMove(
  moveRef: MoveRef | undefined,
): Record<string, Record<string, string>> {
  if (!moveRef) return {};
  return { [moveRef.id]: moveRef.narrativeFieldValues ?? {} };
}

/**
 * Aggiorna `selectedChoices` (gruppo `'main'` virtuale) o
 * `selectedChoiceGroups[groupId]` del `MoveRef`, rispettando la proiezione
 * shorthand → gruppo `'main'`.
 *
 * - `groupId === 'main'`: scrive in `selectedChoices` (shorthand)
 * - altrimenti: scrive in `selectedChoiceGroups[groupId]`
 *
 * Se `moveRef.id !== moveId`, ritorna `moveRef` invariato.
 */
export function updateMoveChoiceGroup(
  moveRef: MoveRef,
  moveId: string,
  groupId: string,
  selectedIds: readonly string[],
): MoveRef {
  if (moveRef.id !== moveId) return moveRef;
  if (groupId === 'main') {
    return { ...moveRef, selectedChoices: [...selectedIds] };
  }
  return {
    ...moveRef,
    selectedChoiceGroups: {
      ...(moveRef.selectedChoiceGroups ?? {}),
      [groupId]: [...selectedIds],
    },
  };
}

/**
 * Aggiorna `narrativeFieldValues[fieldId]` del `MoveRef`. Se
 * `moveRef.id !== moveId`, ritorna `moveRef` invariato.
 */
export function updateMoveNarrativeField(
  moveRef: MoveRef,
  moveId: string,
  fieldId: string,
  value: string,
): MoveRef {
  if (moveRef.id !== moveId) return moveRef;
  return {
    ...moveRef,
    narrativeFieldValues: {
      ...(moveRef.narrativeFieldValues ?? {}),
      [fieldId]: value,
    },
  };
}

/**
 * **D8a** — proietta un `MoveRef` nella shape `Record<moveId, RecruitOutcome>`
 * che `MoveSetupEditor` consuma per il `MoveRecruitOutcomeField`.
 * Ritorna mappa vuota se il `MoveRef` non ha esito (o è `undefined`).
 */
export function buildRecruitOutcomeByMove(
  moveRef: MoveRef | undefined,
): Record<string, RecruitOutcome> {
  if (!moveRef?.recruitOutcome) return {};
  return { [moveRef.id]: moveRef.recruitOutcome };
}

/**
 * **D8a** — aggiorna `recruitOutcome` del `MoveRef`. Quando l'esito cambia
 * (10+ → 7-9 o viceversa), il `pickCount` effettivo del gruppo `main` cambia
 * → bisogna **resettare le selezioni del gruppo `main`** (`selectedChoices`)
 * per evitare di lasciare scelte che violano il nuovo cap. Le altre selezioni
 * (eventuale `selectedChoiceGroups['elemento']` del Druido) sono preservate
 * — non dipendono dall'outcome.
 *
 * Se `moveRef.id !== moveId`, ritorna invariato.
 */
export function updateMoveRecruitOutcome(
  moveRef: MoveRef,
  moveId: string,
  outcome: RecruitOutcome | undefined,
): MoveRef {
  if (moveRef.id !== moveId) return moveRef;
  if (outcome === moveRef.recruitOutcome) return moveRef;
  return {
    ...moveRef,
    selectedChoices: [],
    ...(outcome !== undefined ? { recruitOutcome: outcome } : {}),
  };
}
